import { useEffect, useRef } from 'react';

import { MultiScreenEvent, MultiScreenEventTypes } from 'events/multiScreenEvent';
import { PlayerEvent, PlayerEventsListeners, PlayerEventGroup } from 'shared-types/player-event';

// eslint-disable-next-line complexity
const getPlayerEventGroup = (eventType: PlayerEvent): PlayerEvent | PlayerEventGroup => {
  switch (eventType) {
    case PlayerEvent.PLAY:
    case PlayerEvent.PAUSE:
      return PlayerEventGroup.TOGGLE_PLAY;
    case PlayerEvent.ON_PLAY:
    case PlayerEvent.ON_PAUSE:
      return PlayerEventGroup.ON_TOGGLE_PLAY;
    case PlayerEvent.MUTE_VOCAL_GUIDE:
    case PlayerEvent.UNMUTE_VOCAL_GUIDE:
      return PlayerEventGroup.TOGGLE_MUTE_VOCAL_GUIDE;
    case PlayerEvent.ON_MUTE_VOCAL_GUIDE:
    case PlayerEvent.ON_UNMUTE_VOCAL_GUIDE:
      return PlayerEventGroup.ON_TOGGLE_MUTE_VOCAL_GUIDE;
    default:
      return eventType;
  }
};

const useMultiScreenEventsListener = (
  eventType: MultiScreenEventTypes,
  eventListeners: PlayerEventsListeners,
) => {
  const timestamps = useRef<Record<string, number>>({});

  useEffect(() => {
    const handleMultiScreenEvent = ((event: MultiScreenEvent) => {
      const { actionType, actionData, actionTimestamp } = event;

      const handler = eventListeners[actionType];
      const playerEventGroup = getPlayerEventGroup(actionType);
      const latestPlayerEventGroupTimestamp = timestamps.current[playerEventGroup];

      if (
        handler &&
        (!latestPlayerEventGroupTimestamp || latestPlayerEventGroupTimestamp < actionTimestamp)
      ) {
        handler(new MessageEvent(actionType, { data: actionData }));
        timestamps.current = { ...timestamps.current, [playerEventGroup]: actionTimestamp };
      }
    }) as EventListener;

    document.addEventListener(eventType, handleMultiScreenEvent);

    return () => {
      document.removeEventListener(eventType, handleMultiScreenEvent);
    };
  }, [eventType, eventListeners]);
};

export const useIpadToTvMultiScreenEventsListener = (eventListeners: PlayerEventsListeners) => {
  useMultiScreenEventsListener(MultiScreenEventTypes.IpadToTv, eventListeners);
};

export const useTvToIpadMultiScreenEventsListener = (eventListeners: PlayerEventsListeners) => {
  useMultiScreenEventsListener(MultiScreenEventTypes.TvToIpad, eventListeners);
};
